/**
 * Provision Component - Modernized for Angular 20+
 * Read-only view for a single provision, following the person.component pattern.
 */
import { Component, inject, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Subject, takeUntil, switchMap, of } from 'rxjs';

import { ProvisionsService } from '../../service/entity-services/provisions.service';
import { Provision } from '../../objects/provision';
import { EntityActionsSidenavComponent, EntityAction, EntityTab } from '../../entity-form-components/entity-actions-sidenav/entity-actions-sidenav.component';
import { SidenavContentService } from '../../service/sidenav-content.service';

@Component({
  selector: 'app-provision',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatChipsModule,
    MatTabsModule,
    MatSidenavModule,
    MatToolbarModule,
    EntityActionsSidenavComponent
  ],
  templateUrl: './provision.component.html',
  styleUrls: ['./provision.component.css']
})
export class ProvisionComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private provisionsService = inject(ProvisionsService);
  private sidenavContentService = inject(SidenavContentService);
  private sanitizer = inject(DomSanitizer);
  private destroy$ = new Subject<void>();

  currentProvision: Provision | null = null;
  loading = true;
  error: string | null = null;

  // Display-friendly values
  ownerName = '';
  approverName = '';

  // Safe HTML for rich text fields (bypasses Angular's style sanitization)
  safeDescription: SafeHtml = '';
  safeCommentsRecommendations: SafeHtml = '';

  // Tab management
  selectedTabIndex = 0;

  // Sidenav actions using signals (Angular 20 pattern)
  sidenavActions = signal<EntityAction[]>([
    {
      label: 'Edit',
      icon: 'edit',
      action: () => this.editProvision(),
      disabled: false
    },
    {
      label: 'View Source',
      icon: 'link',
      action: () => this.viewSource(),
      disabled: false
    }
  ]);

  // Tabs configuration using signals
  sidenavTabs = signal<EntityTab[]>([
    { label: 'Details', index: 0, icon: 'info' },
    { label: 'Risk', index: 1, icon: 'assessment' }
  ]);

  ngOnInit() {
    const provNum = this.route.snapshot.paramMap.get('id');

    if (!provNum) {
      this.error = 'No provision number provided';
      this.loading = false;
      return;
    }

    // Register sidenav toggle for hamburger menu
    this.sidenavContentService.registerDetailSidenavToggle(() => {
      // Provision view uses static sidenav, no toggle needed
    });

    this.loadProvisionFromCache(provNum);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.sidenavContentService.unregisterDetailSidenavToggle();
  }

  loadProvisionFromCache(provNum: string) {
    this.loading = true;
    this.error = null;

    this.provisionsService.allProvisions$
      .pipe(
        takeUntil(this.destroy$),
        switchMap(provisions => {
          const provision = provisions.find(p => p.provNum === provNum);

          if (!provision) {
            if (provisions.length === 0) {
              // Provisions still loading - return empty to keep waiting
              return of(null);
            } else {
              // Provision not found
              throw new Error('Provision not found');
            }
          }

          // Return the provision (future: could add hydration here)
          return of(provision);
        })
      )
      .subscribe({
        next: (provision) => {
          if (provision) {
            this.currentProvision = provision;
            this.processProvisionData();
            this.loading = false;
          }
          // If null, keep waiting
        },
        error: (err) => {
          console.error('Error loading provision:', err);
          this.error = err.message || 'Failed to load provision. Please try again.';
          this.loading = false;
        }
      });
  }

  processProvisionData() {
    if (!this.currentProvision) return;

    // Extract display-friendly owner name
    this.ownerName = this.extractDisplayName(
      this.currentProvision.owner,
      'friendlyName',
      'email'
    );

    // Extract display-friendly approver name
    this.approverName = this.extractDisplayName(
      this.currentProvision.approver,
      'friendlyName',
      'email'
    );

    // Sanitize HTML content to allow inline styles (red font, etc.)
    if (this.currentProvision.description) {
      this.safeDescription = this.sanitizer.bypassSecurityTrustHtml(
        this.currentProvision.description
      );
    }

    if (this.currentProvision.commentsRecommendations) {
      this.safeCommentsRecommendations = this.sanitizer.bypassSecurityTrustHtml(
        this.currentProvision.commentsRecommendations
      );
    }
  }

  private extractDisplayName(
    value: any,
    primaryKey: string,
    fallbackKey?: string
  ): string {
    if (!value) return 'N/A';

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'object') {
      if (value[primaryKey]) return value[primaryKey];
      if (fallbackKey && value[fallbackKey]) return value[fallbackKey];
    }

    return 'N/A';
  }

  /**
   * Formats a date string for display using native JavaScript Date APIs.
   */
  formatDate(dateValue: string | undefined): string {
    if (!dateValue) return 'N/A';

    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) {
        return dateValue;
      }
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateValue;
    }
  }

  /**
   * Gets a human-readable label for the risk score.
   */
  getRiskScoreLabel(score: string | number | null | undefined): string {
    const numScore = typeof score === 'string' ? parseInt(score, 10) : score;
    if (numScore === null || numScore === undefined || isNaN(numScore as number)) {
      return 'Not Scored';
    }

    const labels: Record<number, string> = {
      0: 'Not Applicable',
      1: 'Very Low',
      2: 'Low',
      3: 'Medium',
      4: 'High',
      5: 'Very High'
    };

    return labels[numScore as number] || 'Unknown';
  }

  editProvision() {
    if (this.currentProvision?.provNum) {
      this.router.navigate(['/securehome/new-provision', this.currentProvision.provNum]);
    }
  }

  viewSource() {
    if (this.currentProvision?.linkToSource) {
      window.open(this.currentProvision.linkToSource, '_blank');
    } else if (this.currentProvision?.sourceId && this.currentProvision?.sourceType) {
      // Navigate based on source type
      const sourceType = this.currentProvision.sourceType.toLowerCase();
      const sourceId = this.currentProvision.sourceId;

      if (sourceType === 'contract') {
        this.router.navigate(['/securehome/contract', sourceId]);
      } else if (sourceType === 'baa') {
        this.router.navigate(['/securehome/baa-record', sourceId]);
      } else if (sourceType === 'coi') {
        this.router.navigate(['/securehome/coi-record', sourceId]);
      }
    }
  }

  goBack() {
    this.router.navigate(['/securehome/provisions']);
  }

  // Tab selection handler
  selectTab(index: number): void {
    this.selectedTabIndex = index;
  }
}
